import {
  Forward10,
  PauseSharp,
  PlayArrow,
  Replay10,
  SkipNext,
  SkipPrevious,
  Stop,
  ZoomIn,
  ZoomOut,
} from '@mui/icons-material';
import {Box, Typography} from '@mui/material';
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import './ArrAudioWaveform.scss';
import {ArrAVPreviewContext} from '../arr-av-provider/ArrAVPreviewProvider';
import {ArrAudioPayback} from './ArrAudioPayback';
import {ArrAudioVolume} from './ArrAudioVolume';
import {FileTypeCode} from '../../../document-viewer/panels/document-center-panel/DocumentViewerPanel/DocumentMediaViewerPanel';
import {ArrAudioControlButton} from './ArrAudioControlButton';
import {t} from 'i18next';
import RegionsPlugin from '../../../common/arr-waveform/waveform/plugins/regions';
import {useArrWaveform} from '../../../common/arr-waveform/useArrWaveform';
import ArrVideoPlayer from '../video-player/ArrVideoPlayer';
import {isEmpty} from '../../../../utils/formatters';
import {msToTime} from '../../../../utils/utils';

export interface ArrAudioWaveformProps {
  mediaUrl?: Blob;
  videoUrl?: string;
  zoomType?: FileTypeCode;
  waveformImageUrl?: string;
  miniMediaLength?: string;
  miniAudioTrimmedLength?: string;
  miniAudioDocDateTimeOnly?: string;
  onChangeZoomType: (zoomType: FileTypeCode) => void;
}

const DEFAULT_WAVEFORM_WIDTH = 850;
const DEFAULT_WAVEFORM_HEIGHT = 150;

const ArrAudioWaveform: React.FC<ArrAudioWaveformProps> = ({
  mediaUrl,
  videoUrl,
  zoomType,
  waveformImageUrl,
  miniMediaLength,
  miniAudioTrimmedLength,
  miniAudioDocDateTimeOnly,
  onChangeZoomType,
}: ArrAudioWaveformProps) => {
  const avPreviewcontext = useContext(ArrAVPreviewContext);
  const {setIsPlayerReady, setArrWaveformRef, clearAllRegionsMarkers} =
    avPreviewcontext;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<HTMLMediaElement | undefined>(undefined);
  const regions = useMemo(() => RegionsPlugin.create(), []);
  const plugins = useMemo(() => [regions], [regions]);
  const [naturalWidth, setNaturalWidth] = useState<number>(850);
  const [naturalHeight, setNaturalHeight] = useState<number>(150);

  const {arrwaveform, isPlaying, currentTime, duration, isReady} =
    useArrWaveform({
      container: containerRef,
      barWidth: 0,
      waveColor: 'transparent',
      progressColor: 'var(--waveform-progress-color)',
      cursorColor: 'var(--waveform-cursor-color)',
      height: naturalHeight,
      cursorWidth: 4,
      barGap: undefined,
      barRadius: undefined,
      backend: 'MediaElement',
      renderFunction: undefined,
      normalize: false,
      dragToSeek: true,
      interact: true,
      autoCenter: false,
      plugins,
    });

  useEffect(() => {
    clearAllRegionsMarkers();
  }, [mediaUrl, clearAllRegionsMarkers]);

  useEffect(() => {
    if (!arrwaveform) return;
    if (mediaUrl) {
      arrwaveform.seekTo(0);
      arrwaveform.loadBlob(mediaUrl);
    }
  }, [mediaUrl, arrwaveform]);

  useEffect(() => {
    setIsPlayerReady(isReady);
    if (isReady) {
      clearAllRegionsMarkers();
    }
  }, [clearAllRegionsMarkers, isReady, setIsPlayerReady]);

  useEffect(() => {
    if (!arrwaveform) return;
    setArrWaveformRef(arrwaveform);

    arrwaveform.on('finish', function () {
      arrwaveform.seekTo(0);
      arrwaveform.play();
    });

    return () => {
      arrwaveform?.destroy();
      setIsPlayerReady(false);
    };
  }, [setIsPlayerReady, setArrWaveformRef, arrwaveform]);

  const setVideoPlauerRef = useCallback((ref: HTMLMediaElement | undefined) => {
    playerRef.current = ref;
  }, []);

  const handlePlayPause = useCallback(() => {
    arrwaveform?.playPause();
  }, [arrwaveform]);

  const handleStop = useCallback(() => {
    arrwaveform?.stop();
  }, [arrwaveform]);

  const handleReplay10 = useCallback(() => {
    if (arrwaveform) {
      const t = arrwaveform.getCurrentTime();
      arrwaveform.setTime(Math.max(t - 10, 0));
    }
  }, [arrwaveform]);

  const handleForward10 = useCallback(() => {
    if (arrwaveform) {
      const t = arrwaveform.getCurrentTime();
      arrwaveform.setTime(Math.min(t + 10, duration ?? 0));
    }
  }, [arrwaveform, duration]);

  const handleZoom = useCallback(() => {
    if (arrwaveform) {
      const getZoomLevel = zoomType === 'ZoomOut' ? 'ZoomIn' : 'ZoomOut';
      onChangeZoomType?.(getZoomLevel);
    }
  }, [onChangeZoomType, arrwaveform, zoomType]);

  useEffect(() => {
    if (waveformImageUrl) {
      const img = new Image();
      img.onload = () => {
        setNaturalWidth(img.naturalWidth || DEFAULT_WAVEFORM_WIDTH);
        setNaturalHeight(img.naturalHeight || DEFAULT_WAVEFORM_HEIGHT);
      };
      img.src = waveformImageUrl;
    }
  }, [waveformImageUrl]);

  const scaledWidth = useMemo(() => Math.round(naturalWidth), [naturalWidth]);

  const scaledHeight = useMemo(
    () => Math.round(naturalHeight),
    [naturalHeight]
  );

  useEffect(() => {
    if (!isEmpty(waveformImageUrl)) {
      arrwaveform?.setOptions({
        waveformImageUrl,
      });
    }
  }, [arrwaveform, waveformImageUrl]);

  useEffect(() => {
    if (zoomType === 'ZoomIn') {
      arrwaveform?.zoom(scaledWidth / (duration ?? 1));
    } else {
      arrwaveform?.zoom(0);
    }
    arrwaveform?.setOptions({
      height: scaledHeight,
    });
  }, [arrwaveform, duration, scaledHeight, scaledWidth, zoomType]);

  const formatTime = useCallback(
    (t: number) => {
      if (!Number.isFinite(t)) return '00:00:00';
      let total = Math.max(0, Math.floor(t));
      const h = Math.floor(total / 3600);
      total %= 3600;
      const m = Math.floor(total / 60);
      const s = total % 60;
      if ((duration ?? 0) >= 3600 || h > 0) {
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      }
      return `00:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    [duration]
  );

  const msToDuration = useCallback((msStr?: string): number => {
    const parsed = Number(msStr?.trim());
    if (!Number.isFinite(parsed)) return 0;

    const ms = Math.max(0, parsed);
    return Math.floor(ms / 1000);
  }, []);

  // Adds two "hh:mm:ss" strings and returns a zero-padded "hh:mm:ss"
  const addTimes = useCallback((t1: string, t2: string) => {
    const toSeconds = (t: string) => {
      if (typeof t !== 'string') return 0;
      const parts = t.trim().split(':').map(Number);
      if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n)))
        return 0;
      const [h, m, s] = parts;
      return h * 3600 + m * 60 + s;
    };

    const total = toSeconds(t1) + toSeconds(t2);

    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, []);

  const isMediaMoreThanOneHour = useMemo(() => {
    return msToDuration(miniMediaLength) >= 3600;
  }, [miniMediaLength, msToDuration]);

  return (
    <Fragment>
      {videoUrl && (
        <ArrVideoPlayer
          mediaUrl={videoUrl}
          videoPlayerRef={setVideoPlauerRef}
        />
      )}
      <div
        ref={containerRef}
        id={'waveform'}
        data-testid={`arr-audio-waveform-div`}
      />
      <Box className={'audio-time-container'}>
        <Box className={'audio-timer'}>
          <Box className={'start-timer'}>
            <Typography
              className="typo-text body4_size time-text"
              data-testid={`arr-audio-waveform-00-00-00`}
            >
              00:00:00
            </Typography>
            <Typography
              className="typo-text body4_size time-text play-time-text"
              data-testid={`arr-audio-waveform-current-time`}
            >
              {formatTime(currentTime)}
            </Typography>
          </Box>
          <Typography
            className="typo-text body4_size time-text"
            data-testid={`arr-audio-waveform-media-length`}
          >
            {msToTime(miniMediaLength)}
          </Typography>
        </Box>
        {miniAudioTrimmedLength === '0' &&
          miniAudioDocDateTimeOnly &&
          miniAudioDocDateTimeOnly !== '00:00:00' && (
            <Box className={'audio-timer'}>
              <Box className={'start-timer'}>
                <Typography
                  className="typo-text body4_size time-text"
                  data-testid={`arr-audio-waveform-mini-audio-date-time`}
                >
                  {miniAudioDocDateTimeOnly}
                </Typography>
                <Typography
                  className="typo-text body4_size time-text play-time-text"
                  data-testid={`arr-audio-waveform-add-time-current`}
                >
                  {addTimes(miniAudioDocDateTimeOnly, formatTime(currentTime))}
                </Typography>
              </Box>
              <Typography
                className="typo-text body4_size time-text"
                data-testid={`arr-audio-waveform-add-time-length`}
              >
                {addTimes(miniAudioDocDateTimeOnly, msToTime(miniMediaLength))}
              </Typography>
            </Box>
          )}
      </Box>
      <Box className={'arr-audio-waveform-controls'}>
        <Box className={'arr-audio-waveform-controls-container'}>
          <ArrAudioControlButton
            icon={isPlaying ? <PauseSharp /> : <PlayArrow />}
            toolTipText={isPlaying ? t('docMedia.pause') : t('docMedia.play')}
            ariaLabel={isPlaying ? t('docMedia.pause') : t('docMedia.play')}
            onClick={handlePlayPause}
            data-testid="arr-audio-waveform-arr-audio-control-button-play-pause"
          />
          <ArrAudioControlButton
            icon={<Stop />}
            toolTipText={t('docMedia.stop')}
            ariaLabel={t('docMedia.stop')}
            onClick={handleStop}
            data-testid="arr-audio-waveform-arr-audio-control-button-stop"
          />
          <ArrAudioControlButton
            icon={<SkipPrevious />}
            toolTipText={t('docMedia.previousHit')}
            ariaLabel={t('docMedia.previousHit')}
          />
          <ArrAudioControlButton
            icon={<Replay10 />}
            toolTipText={t('docMedia.rewind10s')}
            ariaLabel={t('docMedia.rewind10s')}
            onClick={handleReplay10}
            data-testid="arr-audio-waveform-arr-audio-control-button-rewind"
          />
          <ArrAudioControlButton
            icon={<Forward10 />}
            toolTipText={t('docMedia.forword10s')}
            ariaLabel={t('docMedia.forword10s')}
            onClick={handleForward10}
            data-testid="arr-audio-waveform-arr-audio-control-button-forward"
          />
          <ArrAudioControlButton
            icon={<SkipNext />}
            toolTipText={t('docMedia.nextHit')}
            ariaLabel={t('docMedia.nextHit')}
          />
        </Box>
        <Box className={'arr-audio-waveform-controls-container'}>
          <ArrAudioPayback />
          <ArrAudioVolume />
          <Box className={'divider-vertical'} />
          <ArrAudioControlButton
            disabled={!isMediaMoreThanOneHour}
            icon={zoomType === 'ZoomIn' ? <ZoomIn /> : <ZoomOut />}
            toolTipText={t('docMedia.zoom')}
            ariaLabel={t('docMedia.zoom')}
            onClick={handleZoom}
            data-testid="arr-audio-waveform-arr-audio-control-button-zoom"
          />
        </Box>
      </Box>
    </Fragment>
  );
};

export default ArrAudioWaveform;
